import {useCallback, useEffect} from 'react';

import {useApi} from '~/modules/api';
import {User, authListener, logout} from '~/modules/firebase';
import {useAuth} from '~/store/hooks';

const useAuthListener = () => {
  const {
    userIdToken,
    authStateKnown,
    onSetAuth,
    onSetAdminUser,
    onSetAdminPin,
  } = useAuth();
  const api = useApi();

  const onAuthStateChanged = useCallback(
    async (user: User | null) => {
      if (user) {
        const idToken = await user.getIdToken();
        onSetAuth({
          userId: user.uid,
          userEmail: user.email,
          idToken,
          adminUser: null,
        });
      } else {
        onSetAuth({
          adminUser: null,
        });
      }
    },
    [onSetAuth],
  );

  const onValidateAuth = useCallback(
    async (idToken?: string) => {
      try {
        api.setAuthToken(idToken ?? '');
        if (idToken) {
          const response = await api.fetch({url: '/whoAmI'});
          if (response.data.status === 'success' && response.data.data.admin) {
            onSetAdminUser({
              user: response.data.data.admin,
            });
            onSetAdminPin({
              pin: response.data.data.pin ?? null,
            });
          } else {
            throw new Error(response.data.errorMessage);
          }
        }
      } catch (err) {
        console.error(err);
        await logout();
      }
    },
    [api, onSetAdminUser, onSetAdminPin],
  );

  useEffect(() => {
    const subscriber = authListener(onAuthStateChanged);
    return subscriber;
  }, [onAuthStateChanged]);

  useEffect(() => {
    if (authStateKnown) {
      onValidateAuth(userIdToken);
    }
  }, [userIdToken, authStateKnown, onValidateAuth]);

  return null;
};

export const AuthListener = () => {
  useAuthListener();
  return null;
};
